/**
 * Job Browser Page
 * Browse available job opportunities in the marketplace
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { marketplaceService } from '../../services/marketplace.service';
import type { JobOpportunity } from '../../types/marketplace';
import { ErrorBoundary } from '@/components/ErrorBoundary';

export function JobBrowser() {
  const [jobs, setJobs] = useState<JobOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [county, setCounty] = useState<string>('');

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await marketplaceService.getOpportunities(county || undefined);
      setJobs(response.jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [county]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  return (
    <ErrorBoundary resetKeys={[county]}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Job Marketplace</h1>
          <p className="text-neutral-600">Browse available waste management opportunities</p>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="mb-4">
            <label htmlFor="county" className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by County
            </label>
            <input
              id="county"
              type="text"
              className="input"
              placeholder="Enter county name..."
              value={county}
              onChange={(e) => setCounty(e.target.value)}
            />
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-neutral-600">Loading opportunities...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Job list */}
        {!loading && !error && (
          <div className="space-y-4">
            {jobs.length === 0 ? (
              <div className="card text-center py-12">
                <p className="text-neutral-600">No job opportunities available at this time.</p>
              </div>
            ) : (
              jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/provider/marketplace/jobs/${job.id}`}
                  className="card block hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-neutral-900">{job.projectName}</h3>
                      <p className="text-sm text-neutral-600">{job.projectAddress}</p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        job.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : job.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-neutral-100 text-neutral-800'
                      }`}
                    >
                      {job.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-neutral-600">Target Diversion:</span>
                      <span className="ml-2 font-medium">{job.targetDiversionRate}%</span>
                    </div>
                    <div>
                      <span className="text-neutral-600">Start Date:</span>
                      <span className="ml-2 font-medium">
                        {new Date(job.startDate * 1000).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
